import { ItemThumbnailSize } from 'constants/images';
import { ItemAlertStatus } from 'constants/item';
import { formatCurrencyAmount } from 'libs/utils/formatString';
import { transformAlertToItemStatus } from 'data/transformers/item-status';
import { transformCurrencyAmountDto } from 'data/transformers/currency-amount';

const getItemThumbnail = function (photos) {
  let size = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ItemThumbnailSize.Medium;
  if (!photos) return null;
  const photo = photos.find(_ref => {
    let {
      is_main
    } = _ref;
    return is_main;
  });
  if (!photo) return null;
  const thumbnails = photo.thumbnails || [];
  const thumbnail = thumbnails.find(thumb => thumb.type === size);
  if (!thumbnail) return null;
  return thumbnail.url;
};

export const getItemPhotoThumbnail = function (photos) {
  var _photos$find, _photos$find$thumbnai, _photos$find$thumbnai2;

  let size = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ItemThumbnailSize.Medium;
  return (photos === null || photos === void 0 ? void 0 : (_photos$find = photos.find(photo => photo.isMain)) === null || _photos$find === void 0 ? void 0 : (_photos$find$thumbnai = _photos$find.thumbnails) === null || _photos$find$thumbnai === void 0 ? void 0 : (_photos$find$thumbnai2 = _photos$find$thumbnai.find(thumb => thumb.type === size)) === null || _photos$find$thumbnai2 === void 0 ? void 0 : _photos$find$thumbnai2.url) || null;
};

const getItemPhotosThumbnails = function (photos) {
  let size = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ItemThumbnailSize.Medium;
  if (photos.length === 0) return undefined;
  return photos.map(photo => {
    var _photo$thumbnails$fin;

    return (_photo$thumbnails$fin = photo.thumbnails.find(thumb => thumb.type === size)) === null || _photo$thumbnails$fin === void 0 ? void 0 : _photo$thumbnails$fin.url;
  }).filter(photoUrl => !!photoUrl);
};

const getItemStatus = function (item) {
  let showSensitive = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  const {
    is_draft,
    is_reserved,
    is_hidden,
    is_closed,
    item_closing_action,
    stats_visible,
    item_alert_type,
    item_alert,
    is_processing,
    promoted,
    vas_gallery_promoted
  } = item;
  if (is_processing) return 'processing';

  if (item_alert !== null && item_alert !== void 0 && item_alert.item_alert_type && showSensitive && item_alert.item_alert_type !== ItemAlertStatus.EligibleForAuthenticity && item_alert.item_alert_type !== ItemAlertStatus.VerifiedOnlineAuthenticity) {
    return transformAlertToItemStatus(item_alert.item_alert_type);
  }

  if (item_alert_type && showSensitive) return transformAlertToItemStatus(item_alert_type);
  if (is_draft) return 'draft';

  if (is_closed) {
    return item_closing_action;
  }

  if (is_reserved) return 'reserved';
  if (is_hidden) return 'hidden';
  if (!stats_visible || !showSensitive) return null;
  if (vas_gallery_promoted) return 'promoted';
  if (promoted) return 'bumped';
  return null;
};

const getProductItemStatus = function (item) {
  let showSensitive = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  const {
    isDraft,
    isReserved,
    isHidden,
    isClosed,
    canPushUp,
    canVasGalleryPromote,
    areStatsVisible,
    itemAlertType,
    itemAlert,
    itemClosingAction,
    isProcessing,
    isPromoted,
    isVasGalleryPromoted
  } = item;
  if (isProcessing) return 'processing';

  if (itemAlert !== null && itemAlert !== void 0 && itemAlert.itemAlertType && showSensitive && itemAlert.itemAlertType !== ItemAlertStatus.EligibleForAuthenticity && itemAlert.itemAlertType !== ItemAlertStatus.VerifiedOnlineAuthenticity) {
    return transformAlertToItemStatus(itemAlert.itemAlertType);
  }

  if (itemAlertType && showSensitive) return transformAlertToItemStatus(itemAlertType);
  if (isDraft) return 'draft';

  if (isClosed && itemClosingAction) {
    return itemClosingAction;
  }

  if (isReserved) return 'reserved';
  if (isHidden) return 'hidden';
  if (!areStatsVisible) return null;
  if (!showSensitive) return null;
  if (canPushUp === canVasGalleryPromote && canPushUp) return null;
  if (isVasGalleryPromoted) return 'promoted';
  if (isPromoted) return 'bumped';
  return 'promoted';
};

const getItemStatusMessage = status => {
  if (!status) return null;
  let theme = null;

  switch (status) {
    case 'processing':
      return {
        content: 'item.status.processing',
        theme: 'amplified'
      };

    case 'reserved':
      theme = 'amplified';
      break;

    case 'promoted':
      theme = 'primary';
      break;

    case 'bumped':
      theme = 'primary';
      break;

    case 'draft':
    case 'hidden':
      theme = 'muted';
      break;

    case 'sold':
      theme = 'success';
      break;

    case 'missing_subcategory':
    case 'change_description':
    case 'hidden_from_catalog':
    case 'package_size':
      theme = 'warning';
      break;

    case 'delayed':
      return {
        content: 'item.status.delayed',
        theme: 'amplified'
      };

    case ItemAlertStatus.UnderReview:
      return {
        content: 'item.status.under_review',
        theme: 'amplified'
      };

    case ItemAlertStatus.ReplicaProof:
      return {
        content: 'item.status.hidden',
        theme: 'warning'
      };

    default:
      theme = null;
  }

  if (theme === null) return null;
  return {
    content: "item.status.".concat(status),
    theme
  };
};

const toItemPreviewProps = (locale, item, discount) => {
  let price = discount ? transformCurrencyAmountDto(discount.discount_price) : null;

  if (!price) {
    price = item.priceWithDiscount || item.price;
  }

  const oldPrice = item.price.amount === price.amount ? null : item.price;
  return {
    id: item.id,
    photoUrl: getItemPhotoThumbnail(item.photos),
    price: formatCurrencyAmount(price, locale),
    oldPrice: oldPrice ? formatCurrencyAmount(oldPrice, locale) : null
  };
};

const toItemsPreviewProps = (locale, items, discounts) => items.map(item => {
  const discount = discounts === null || discounts === void 0 ? void 0 : discounts.find(dis => dis.item_id === item.id);
  return toItemPreviewProps(locale, item, discount);
});

const mergeItemsWithDrafts = response => [...response.drafts, ...response.items];

export { getItemThumbnail, getItemPhotosThumbnails, getItemStatus, getItemStatusMessage, getProductItemStatus, toItemsPreviewProps, mergeItemsWithDrafts };